import stylesheet from './Link.scss?inline';

import CustomComponent from "../../base/customComponent/CustomComponent";
import {getIconStyles} from "../../util/lazy";

class NXTLink extends CustomComponent {
    static observedAttributes  = ['href'];
    private static readonly passThroughArguments = ['href', 'target', 'rel', 'download', 'aria-disabled'];
    private linkingElement: HTMLAnchorElement | HTMLButtonElement;

    constructor() {
        super();
        this.customStyle = stylesheet;
        void this.appendStyleLazy(getIconStyles);
    }

    get tagName(): 'a' | 'button' {
        if (this.hasAttribute('tag')) {
            const tag = this.getAttribute('tag');
            if (['a', 'button'].includes(tag)) {
                return tag as ('a' | 'button');
            }
        }
        return 'a';
    }

    passThroughProperties(element: HTMLAnchorElement | HTMLButtonElement) {
        this.getAttributeNames()
            .filter(existingArgument => NXTLink.passThroughArguments.includes(existingArgument))
            .forEach(attributeName => {
                element.setAttribute(attributeName, this.getAttribute(attributeName));
            });
    }

    isValidLinkSize(size: string): boolean {
        return ['xs', 'sm', 'md'].includes(size);
    }

    get linkSize(): string {
        if (this.hasAttribute('size')) {
            const size = this.getAttribute('size');
            if (this.isValidLinkSize(size)) {
                return size;
            }
        }
        return 'sm';
    }

    get linkType(): string {
        if (this.hasAttribute('type')) {
            const type = this.getAttribute('type');
            if (type === 'secondary' || type === 'black') {
                return 'secondary';
            }
            if (type === 'primary') {
                return 'primary';
            }
        }
        return 'primary';
    }

    get linkVariant(): string {
        if (this.hasAttribute('variant')) {
            const variant = this.getAttribute('variant');
            if (['default', 'inline'].includes(variant)) {
                return variant;
            }
        }
        return 'default';
    }

    get linkIconLeft(): string | undefined {
        if (this.hasAttribute('icon-left')) {
            return this.getAttribute('icon-left');
        }
        return undefined;
    }

    get linkIconRight(): string | undefined {
        if (this.hasAttribute('icon-right')) {
            return this.getAttribute('icon-right');
        }
        return undefined;
    }

    getLinkBreakpointSizeClasses(): string[] {
        const additionalClasses: string[] = [];
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `size-${breakpoint}`;
            if (this.hasAttribute(attributeName) && this.isValidLinkSize(this.getAttribute(attributeName))) {
                additionalClasses.push(`link-${this.getAttribute(attributeName)}-${breakpoint}-up`);
            }
        }
        return additionalClasses;
    }

    getLinkClasses(): string[] {
        const classList = ['link', `link-${this.linkSize}`, `link-${this.linkVariant}`, ...this.getLinkBreakpointSizeClasses(), `link-${this.linkType}`];
        if (this.linkIconLeft) {
            classList.push('link-icon-left', `icon-before-${this.linkIconLeft}`);
        }
        if (this.linkIconRight) {
            classList.push('link-icon-right', `icon-after-${this.linkIconRight}`);
        }
        return classList;
    }

    render() {
        this.linkingElement = document.createElement(this.tagName);
        this.linkingElement.classList.add(...this.getLinkClasses());
        this.passThroughProperties(this.linkingElement);
        this.linkingElement.innerHTML = `<span class="link-text"><slot></slot></span>`;
        return this.linkingElement;
    }

    // noinspection JSUnusedGlobalSymbols
    attributeChangedCallback(attributeName: string, _oldValue: string, newValue: string) {
        if (attributeName === 'href' && this.linkingElement) {
            this.linkingElement.setAttribute('href', newValue);
        }
    }

}

customElements.define('nxt-link', NXTLink);
